import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useTranslation } from '../hooks/useTranslation';
import {
  captureScreenshot,
  collectConsoleLogs,
  collectNetworkLogs,
  getCurrentUrl,
  getBrowserInfo,
} from '../utils/bugReportUtils';
import { ScreenshotAnnotationCanvas } from './ScreenshotAnnotationCanvas';
import { useToast } from '../hooks/useToast';
import { useUserStore } from '../stores/useUserStore';
import { useSensitiveDataBlur } from '../hooks/useSensitiveDataBlur';
import { ReAuthenticationDialog } from './ReAuthenticationDialog';
import {
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Circle,
  ArrowRight,
  Square,
  PenTool,
  Trash2,
  Eye,
  EyeOff,
} from 'lucide-react';

interface BugReportDialogProps {
  open: boolean;
  onClose: () => void;
}

interface Annotation {
  type: 'circle' | 'arrow' | 'rectangle' | 'custom';
  x: number;
  y: number;
  width?: number;
  height?: number;
  radius?: number;
  points?: Array<{ x: number; y: number }>;
  number?: number;
}

export const BugReportDialog: React.FC<BugReportDialogProps> = ({
  open,
  onClose,
}) => {
  return <BugReportDialogContent open={open} onClose={onClose} />;
};

const BugReportDialogContent: React.FC<BugReportDialogProps> = ({
  open,
  onClose,
}) => {
  const { translate } = useTranslation();
  const toast = useToast();
  const { user } = useUserStore();
  const [description, setDescription] = useState('');
  const [expectation, setExpectation] = useState('');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [screenshotVersion, setScreenshotVersion] = useState(0);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [drawingMode, setDrawingMode] = useState<
    'circle' | 'arrow' | 'rectangle' | 'custom' | null
  >(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const getCanvasDataUrlRef = useRef<(() => string | null) | null>(null);
  const screenshotRef = useRef<string | null>(null); // Track screenshot in ref to avoid stale closures
  const screenshotTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Timeout to clear screenshot after dialog closes
  const descriptionEditableRef = useRef<HTMLDivElement>(null);
  const isUpdatingDescriptionRef = useRef(false);
  const descriptionRef = useRef<string>('');

  const {
    activateBlur,
    deactivateBlur,
    revealBlurredData,
    hideRevealedData,
    isRevealed,
    isBlurActive,
  } = useSensitiveDataBlur();
  const [showReAuthDialog, setShowReAuthDialog] = useState(false);

  const handleCaptureScreenshot = useCallback(async () => {
    try {
      setIsCapturing(true);
      console.log('📸 Starting screenshot capture...');

      // Activate blur before capturing screenshot
      activateBlur();

      // Wait a brief moment for blur to apply
      await new Promise((resolve) => setTimeout(resolve, 100));

      const screenshotData = await captureScreenshot();

      // Don't deactivate blur if data is revealed - keep it active
      // Only deactivate if not revealed
      if (!isRevealed) {
        deactivateBlur();
      }
      console.log(
        '📸 Screenshot captured, length:',
        screenshotData?.length,
        'starts with:',
        screenshotData?.substring(0, 50)
      );
      if (screenshotData && screenshotData.length > 0) {
        console.log(
          '📸 Setting screenshot state, data length:',
          screenshotData.length
        );
        // Set screenshot state - use direct assignment, not functional update
        // to ensure we always set the new value
        screenshotRef.current = screenshotData; // Update ref immediately
        setScreenshot(screenshotData);
        setScreenshotVersion((prev) => {
          const newVersion = prev + 1;
          console.log('📸 Screenshot version updated:', prev, '->', newVersion);
          return newVersion;
        });
        console.log('📸 Screenshot state updated successfully');
      } else {
        console.error('❌ Screenshot data is empty or invalid');
        console.error('📸 Data details:', {
          exists: !!screenshotData,
          length: screenshotData?.length,
          type: typeof screenshotData,
        });
        toast.error(
          translate(
            'bugReport.screenshotEmpty',
            'Screenshot capture returned empty data'
          )
        );
      }
    } catch (error) {
      console.error('❌ Failed to capture screenshot:', error);
      toast.error(
        translate('bugReport.captureFailed', 'Failed to capture screenshot')
      );
      // Ensure blur is deactivated even on error (unless revealed)
      if (!isRevealed) {
        deactivateBlur();
      }
    } finally {
      setIsCapturing(false);
    }
  }, [toast, activateBlur, deactivateBlur, isRevealed]);

  // Reset state when dialog opens and wait for dialog to stabilize before capturing
  useEffect(() => {
    if (open) {
      // Check for pre-captured screenshot (captured before dialog opened)
      const preCapturedScreenshot = (window as any).__preCapturedScreenshot;
      if (preCapturedScreenshot) {
        console.log('[Bug Report] Using pre-captured screenshot');
        screenshotRef.current = preCapturedScreenshot;
        setScreenshot(preCapturedScreenshot);
        setScreenshotVersion((prev) => prev + 1);
        // Clear the global variable so it's not reused
        delete (window as any).__preCapturedScreenshot;
        // Still reset other state
        setAnnotations([]);
        setDescription('');
        setExpectation('');
        setDrawingMode(null);
        setZoomLevel(1);
        setPanPosition({ x: 0, y: 0 });
        return; // Don't capture again, we already have the screenshot
      }

      // Only reset screenshot if we don't already have one (preserve existing screenshot)
      // Reset other state when dialog opens
      setAnnotations([]);
      setDescription('');
      setExpectation('');
      setDrawingMode(null);
      setZoomLevel(1);
      setPanPosition({ x: 0, y: 0 });
      // Clear any pending timeout
      if (screenshotTimeoutRef.current) {
        clearTimeout(screenshotTimeoutRef.current);
        screenshotTimeoutRef.current = null;
      }

      // Screenshot should be cleared when dialog closes, so we don't need to restore it
      // If screenshot exists, it means it was just captured and should be preserved

      // Mark the backdrop overlay with a data attribute so we can identify it in screenshots
      // The overlay is a sibling of the dialog content in the same portal
      const markBackdrop = () => {
        const dialogContent = document.querySelector(
          '[data-bug-report-dialog="true"]'
        );
        if (!dialogContent) {
          console.log('⚠️ Bug report dialog content not found');
          return false;
        }
        const portal = dialogContent.closest('[data-radix-portal]');
        if (!portal) {
          console.log('⚠️ Bug report portal not found');
          return false;
        }
        const overlay = portal.querySelector('[data-radix-dialog-overlay]');
        if (overlay) {
          (overlay as HTMLElement).setAttribute(
            'data-bug-report-backdrop',
            'true'
          );
          console.log('✅ Marked bug report backdrop:', {
            tag: overlay.tagName,
            classes: (overlay as HTMLElement).className,
          });
          return true;
        } else {
          console.log('⚠️ Overlay not found in portal');
          return false;
        }
      };

      // Try to mark backdrop multiple times with increasing delays
      // This ensures we catch it even if DOM isn't ready immediately
      let attempts = 0;
      const maxAttempts = 10;
      const tryMarkBackdrop = () => {
        attempts++;
        if (markBackdrop()) {
          return; // Success, stop trying
        }
        if (attempts < maxAttempts) {
          setTimeout(tryMarkBackdrop, 50 * attempts); // Exponential backoff
        } else {
          console.warn(
            '⚠️ Could not find backdrop after',
            maxAttempts,
            'attempts'
          );
        }
      };

      // Start trying immediately and continue with delays
      tryMarkBackdrop();
      setTimeout(tryMarkBackdrop, 50);
      setTimeout(tryMarkBackdrop, 100);
      setTimeout(tryMarkBackdrop, 200);

      // Wait for dialog to fully render and stabilize, then capture screenshot
      // The capture will hide the dialog during the process
      // Use requestAnimationFrame to ensure DOM is updated, then wait additional time
      let rafId1: number | null = null;
      let rafId2: number | null = null;
      let timeoutId: NodeJS.Timeout | null = null;

      rafId1 = requestAnimationFrame(() => {
        rafId2 = requestAnimationFrame(() => {
          // Double RAF ensures layout is complete, then add delay for animations
          timeoutId = setTimeout(() => {
            // Final attempt to mark backdrop right before screenshot
            const dialogContent = document.querySelector(
              '[data-bug-report-dialog="true"]'
            );
            if (dialogContent) {
              const portal = dialogContent.closest('[data-radix-portal]');
              if (portal) {
                const overlay = portal.querySelector(
                  '[data-radix-dialog-overlay]'
                );
                if (overlay) {
                  (overlay as HTMLElement).setAttribute(
                    'data-bug-report-backdrop',
                    'true'
                  );
                  console.log('✅ Final backdrop marking before screenshot');
                }
              }
            }

            // Only capture if we don't already have a screenshot
            // Use ref.current to get the most up-to-date value (avoids stale closures)
            // Also check if screenshot state exists (though ref is the source of truth)
            const hasScreenshot = screenshotRef.current;
            if (!hasScreenshot) {
              console.log(
                '📸 Dialog opened and stabilized, capturing screenshot...'
              );
              // Call the capture function - it's async but we don't need to await it
              // The function handles its own state updates
              handleCaptureScreenshot().catch((err) => {
                console.error('❌ Auto-capture failed:', err);
              });
            } else {
              console.log(
                '📸 Screenshot already exists (from ref), skipping auto-capture'
              );
            }
          }, 800); // Increased delay to 800ms to ensure dialog is fully stable
        });
      });

      return () => {
        if (rafId1) {
          cancelAnimationFrame(rafId1);
        }
        if (rafId2) {
          cancelAnimationFrame(rafId2);
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    } else {
      // Clean up state when dialog closes
      // Clear screenshot immediately so fresh capture happens on next open
      setAnnotations([]);
      setDescription('');
      setExpectation('');
      setDrawingMode(null);
      setZoomLevel(1);
      setPanPosition({ x: 0, y: 0 });

      // Clear any existing timeout
      if (screenshotTimeoutRef.current) {
        clearTimeout(screenshotTimeoutRef.current);
        screenshotTimeoutRef.current = null;
      }

      // Clear screenshot immediately when dialog closes
      // This ensures fresh capture on next open, especially after navigation
      console.log('📸 Dialog closed, clearing screenshot');
      setScreenshot(null);
      setScreenshotVersion(0);
      screenshotRef.current = null;
    }

    // Cleanup on unmount (e.g., when navigating away)
    return () => {
      if (screenshotTimeoutRef.current) {
        clearTimeout(screenshotTimeoutRef.current);
        screenshotTimeoutRef.current = null;
      }
      // Clear screenshot on unmount to prevent stale data
      screenshotRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Debug: Log when screenshot state changes
  useEffect(() => {
    console.log('📸 Screenshot state changed:', {
      hasScreenshot: !!screenshot,
      screenshotLength: screenshot?.length,
      screenshotVersion,
      isCapturing,
      dialogOpen: open,
    });
  }, [screenshot, screenshotVersion, isCapturing, open]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setScreenshot(e.target?.result as string);
        setScreenshotVersion((prev) => prev + 1);
      };
      reader.readAsDataURL(file);
    }
  };

  // Parse annotation markers from text: [[number: content]]
  const parseAnnotationMarkers = useCallback((text: string) => {
    const markers: Array<{ number: number; content: string }> = [];
    const regex = /\[\[(\d+):\s*([^\]]+)\]\]/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
      markers.push({
        number: parseInt(match[1], 10),
        content: match[2].trim(),
      });
    }
    return markers;
  }, []);

  const handleAnnotationAdd = useCallback(
    (annotation: Annotation) => {
      // Sync description with current div content first to capture any unsynced typing
      const div = descriptionEditableRef.current;
      let currentText = descriptionRef.current || description;

      if (div && !isUpdatingDescriptionRef.current) {
        // Extract current text from div to get latest user typing
        // Skip text nodes inside badge elements
        const isInsideBadge = (node: Node): boolean => {
          let parent = node.parentElement;
          while (parent && parent !== div) {
            if (parent.hasAttribute('data-annotation')) {
              return true;
            }
            parent = parent.parentElement;
          }
          return false;
        };

        const walker = document.createTreeWalker(
          div,
          NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
          {
            acceptNode: (node) => {
              if (node.nodeType === Node.TEXT_NODE && isInsideBadge(node)) {
                return NodeFilter.FILTER_REJECT;
              }
              return NodeFilter.FILTER_ACCEPT;
            },
          }
        );
        let extractedText = '';
        let node;
        const existingMarkers = parseAnnotationMarkers(
          descriptionRef.current || description
        );

        while ((node = walker.nextNode())) {
          if (node.nodeType === Node.TEXT_NODE) {
            extractedText += node.textContent;
          } else if (
            node.nodeType === Node.ELEMENT_NODE &&
            (node as Element).hasAttribute('data-annotation')
          ) {
            const num = (node as Element).getAttribute('data-annotation');
            if (num) {
              const existingMarker = existingMarkers.find(
                (m) => m.number === parseInt(num, 10)
              );
              if (existingMarker) {
                extractedText += `[[${num}: ${existingMarker.content}]]`;
              } else {
                const badgeElement = node as Element;
                const typeSpan = badgeElement.querySelector('.text-red-600');
                const content = typeSpan?.textContent?.trim() || '';
                extractedText += `[[${num}: ${content}]]`;
              }
            }
          }
        }
        // Only use extracted text if it's actually different and longer (has new typing)
        // This prevents re-extracting already-synced content
        if (extractedText && extractedText.length > currentText.length) {
          currentText = extractedText;
          // Update description immediately to sync it
          setDescription(extractedText);
          descriptionRef.current = extractedText;
        }
      }

      setAnnotations((prev) => {
        const newNumber = prev.length + 1;
        const numberedAnnotation = { ...annotation, number: newNumber };

        // Add annotation marker to current text
        // Always ensure there's a newline before the marker
        // Preserve all newlines - don't trim
        const needsNewline = currentText && !currentText.endsWith('\n');
        // Use new format: [[number: type]]
        const annotationType = translate(
          `bugReport.annotationType.${annotation.type}`,
          annotation.type
        );
        const updatedDescription = needsNewline
          ? `${currentText}\n[[${newNumber}: ${annotationType}]] `
          : `${currentText}[[${newNumber}: ${annotationType}]] `;

        // Update both state and ref to ensure render effect has latest value
        setDescription(updatedDescription);
        descriptionRef.current = updatedDescription;

        return [...prev, numberedAnnotation];
      });
    },
    [description, translate, parseAnnotationMarkers]
  );

  const handleClearAnnotations = () => {
    setAnnotations([]);
  };

  const handleRemoveAnnotation = useCallback((number: number) => {
    setAnnotations((prev) => {
      const filtered = prev.filter((ann) => ann.number !== number);

      // First, remove the marker for the deleted annotation
      setDescription((prevDesc) => {
        let updated = prevDesc.replace(
          new RegExp(`\\[\\[${number}:\\s*[^\\]]+\\]\\]`, 'g'),
          ''
        );

        // Now renumber remaining annotations in order
        // Get all remaining annotation numbers, sorted
        const remainingNumbers = filtered
          .map((ann) => ann.number || 0)
          .filter((n) => n > 0)
          .sort((a, b) => a - b);

        // Renumber from highest to lowest to avoid conflicts
        for (let i = remainingNumbers.length - 1; i >= 0; i--) {
          const oldNumber = remainingNumbers[i];
          const newNumber = i + 1;
          if (oldNumber !== newNumber) {
            updated = updated.replace(
              new RegExp(`\\[\\[${oldNumber}:\\s*([^\\]]+)\\]\\]`, 'g'),
              (match, content) => `[[${newNumber}: ${content.trim()}]]`
            );
          }
        }

        return updated;
      });

      // Renumber remaining annotations
      const renumbered = filtered.map((ann, index) => ({
        ...ann,
        number: index + 1,
      }));

      return renumbered;
    });
  }, []);

  // Keep description ref in sync
  useEffect(() => {
    descriptionRef.current = description;
  }, [description]);

  // Sync annotations with text markers - text is source of truth
  useEffect(() => {
    if (isUpdatingDescriptionRef.current) return;

    // Parse all markers from description text
    const textMarkers = parseAnnotationMarkers(description);

    // Deduplicate markers - keep only the first occurrence of each number
    const uniqueMarkers = new Map<
      number,
      { number: number; content: string }
    >();
    textMarkers.forEach((marker) => {
      if (!uniqueMarkers.has(marker.number)) {
        uniqueMarkers.set(marker.number, marker);
      }
    });
    const deduplicatedMarkers = Array.from(uniqueMarkers.values());

    // If there are duplicates, clean up the description
    if (deduplicatedMarkers.length !== textMarkers.length) {
      let cleanedDescription = description;
      // Remove all duplicate markers, keeping only the first occurrence
      const seenNumbers = new Set<number>();
      cleanedDescription = cleanedDescription.replace(
        /\[\[(\d+):\s*([^\]]+)\]\]/g,
        (match, num) => {
          const number = parseInt(num, 10);
          if (seenNumbers.has(number)) {
            return ''; // Remove duplicate
          }
          seenNumbers.add(number);
          return match; // Keep first occurrence
        }
      );
      // Clean up extra whitespace/newlines (but preserve trailing newlines)
      cleanedDescription = cleanedDescription.replace(/\n{3,}/g, '\n\n');
      setDescription(cleanedDescription);
      descriptionRef.current = cleanedDescription;
      return; // Exit early, will re-run after description updates
    }

    const markerNumbers = new Set(deduplicatedMarkers.map((m) => m.number));

    // Remove annotations that don't have markers in text
    setAnnotations((prev) => {
      const filtered = prev.filter((ann) => markerNumbers.has(ann.number || 0));

      // If annotations were removed, return filtered
      if (filtered.length !== prev.length) {
        return filtered;
      }

      return prev;
    });
  }, [description, parseAnnotationMarkers]);

  // Track previous annotations to detect changes
  const prevAnnotationsRef = useRef<typeof annotations>([]);

  // Update contentEditable div when annotations change or description has new markers
  useEffect(() => {
    const div = descriptionEditableRef.current;
    if (!div || isUpdatingDescriptionRef.current) return;

    // Check if annotations changed
    const annotationsChanged =
      prevAnnotationsRef.current.length !== annotations.length ||
      prevAnnotationsRef.current.some(
        (prev, index) =>
          prev.number !== annotations[index]?.number ||
          prev.type !== annotations[index]?.type
      );

    // Only rebuild if annotations changed
    if (!annotationsChanged) {
      prevAnnotationsRef.current = annotations;
      return;
    }

    prevAnnotationsRef.current = annotations;

    // Use descriptionRef to get latest description without triggering rebuilds on every change
    const textToUse = descriptionRef.current || description;

    // Parse markers from text (source of truth)
    const textMarkers = parseAnnotationMarkers(textToUse);

    // Build HTML with annotation badges - only for markers that can be parsed
    // Ensure each marker is on a new line
    const html = textToUse
      .split(/(\[\[\d+:\s*[^\]]+\]\])/)
      .map((part, index, array) => {
        const match = part.match(/\[\[(\d+):\s*([^\]]+)\]\]/);
        if (match) {
          const annotationNumber = parseInt(match[1], 10);
          const content = match[2].trim();
          // Only render if we can parse it and annotation exists
          const marker = textMarkers.find((m) => m.number === annotationNumber);
          const annotation = annotations.find(
            (ann) => ann.number === annotationNumber
          );
          if (marker && annotation) {
            // Check if we need to add a newline before this badge
            const prevPart = index > 0 ? array[index - 1] : '';
            const needsNewline =
              prevPart &&
              !prevPart.endsWith('\n') &&
              !prevPart.match(/<br\s*\/?>$/);
            const newlineBefore = needsNewline ? '<br>' : '';

            return `${newlineBefore}<span data-annotation="${annotationNumber}" class="inline-flex items-center gap-1.5 px-1.5 py-0.5 mx-1 bg-blue-50 border border-blue-200 rounded-md text-xs font-medium align-middle shadow-sm" contenteditable="false">
            <span class="flex items-center justify-center w-4 h-4 rounded-full bg-blue-500 text-white text-[10px] font-bold leading-none">${annotationNumber}</span>
            <span class="text-blue-700 capitalize">${content}</span>
            <button type="button" class="flex items-center justify-center ml-0.5 hover:bg-blue-200 rounded p-0.5 transition-colors opacity-70 hover:opacity-100" data-remove-annotation="${annotationNumber}" title="Remove this annotation">
              <svg class="h-2.5 w-2.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </span>`;
          }
          // If marker can't be matched to annotation, render as plain text
          return part
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
        }
        // Escape HTML in text parts
        return part
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>');
      })
      .join('');

    // Save cursor position and selection
    const selection = window.getSelection();
    let savedRange: { start: number; end: number } | null = null;

    if (selection && selection.rangeCount > 0) {
      const currentRange = selection.getRangeAt(0);
      // Check if selection is within our div
      if (
        div.contains(currentRange.startContainer) ||
        div === currentRange.startContainer
      ) {
        try {
          const divRange = document.createRange();
          divRange.selectNodeContents(div);
          divRange.setEnd(
            currentRange.startContainer,
            currentRange.startOffset
          );
          const startOffset = divRange.toString().length;
          const endOffset = startOffset + currentRange.toString().length;
          savedRange = { start: startOffset, end: endOffset };
        } catch (e) {
          // Range might be invalid, ignore
        }
      }
    }

    isUpdatingDescriptionRef.current = true;
    div.innerHTML = html;

    // Restore cursor position
    if (savedRange && selection) {
      try {
        const walker = document.createTreeWalker(
          div,
          NodeFilter.SHOW_TEXT,
          null
        );
        let currentOffset = 0;
        let startNode: Node | null = null;
        let startOffset = 0;
        let endNode: Node | null = null;
        let endOffset = 0;

        while (walker.nextNode()) {
          const node = walker.currentNode;
          const nodeLength = node.textContent?.length || 0;
          if (!startNode && currentOffset + nodeLength >= savedRange.start) {
            startNode = node;
            startOffset = Math.max(0, savedRange.start - currentOffset);
          }
          if (currentOffset + nodeLength >= savedRange.end) {
            endNode = node;
            endOffset = Math.max(0, savedRange.end - currentOffset);
            break;
          }
          currentOffset += nodeLength;
        }

        if (startNode && endNode) {
          const newRange = document.createRange();
          newRange.setStart(
            startNode,
            Math.min(startOffset, startNode.textContent?.length || 0)
          );
          newRange.setEnd(
            endNode,
            Math.min(endOffset, endNode.textContent?.length || 0)
          );
          selection.removeAllRanges();
          selection.addRange(newRange);
        } else if (startNode) {
          // Fallback: just set cursor at start position
          const newRange = document.createRange();
          newRange.setStart(
            startNode,
            Math.min(startOffset, startNode.textContent?.length || 0)
          );
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      } catch (e) {
        // Failed to restore cursor, ignore
      }
    }

    // Re-attach click handlers for remove buttons
    div.querySelectorAll('[data-remove-annotation]').forEach((button) => {
      // Remove old listener if any
      const newButton = button.cloneNode(true);
      button.parentNode?.replaceChild(newButton, button);

      newButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const num = parseInt(
          (newButton as Element).getAttribute('data-remove-annotation') || '0',
          10
        );
        if (num) {
          handleRemoveAnnotation(num);
        }
      });
    });

    // Use setTimeout to reset the flag after React's render cycle
    setTimeout(() => {
      isUpdatingDescriptionRef.current = false;
    }, 0);
    // description is intentionally omitted - we use descriptionRef to get latest value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotations, parseAnnotationMarkers, handleRemoveAnnotation]);

  // Handle space key for panning
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in an input or textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault(); // Prevent page scroll
        setIsSpacePressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.code === 'Space') {
        setIsSpacePressed(false);
        // Stop panning when space is released
        setIsPanning(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Handle wheel events with native listener to properly prevent dialog scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheelNative = (e: WheelEvent) => {
      // Always prevent dialog scroll when mouse is over canvas area
      e.preventDefault();
      e.stopPropagation();

      // Don't zoom if in drawing mode (unless space is held)
      if (drawingMode && !isSpacePressed) {
        return; // Still prevent scroll, but don't zoom
      }

      // Calculate zoom delta
      const zoomDelta = e.deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Math.max(0.25, Math.min(3, zoomLevel + zoomDelta));

      // Get mouse position relative to the container
      const rect = container.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Calculate the point in the image coordinates before zoom
      const imageX = (mouseX - panPosition.x) / zoomLevel;
      const imageY = (mouseY - panPosition.y) / zoomLevel;

      // Calculate new pan position to keep the point under the cursor in place
      const newPanX = mouseX - imageX * newZoom;
      const newPanY = mouseY - imageY * newZoom;

      setZoomLevel(newZoom);
      setPanPosition({ x: newPanX, y: newPanY });

      // Reset pan position if zoomed back to 1x
      if (newZoom === 1) {
        setPanPosition({ x: 0, y: 0 });
      }
    };

    // Use capture phase and non-passive to ensure preventDefault works
    container.addEventListener('wheel', handleWheelNative, {
      passive: false,
      capture: true,
    });

    return () => {
      container.removeEventListener('wheel', handleWheelNative, {
        capture: true,
      } as EventListenerOptions);
    };
  }, [drawingMode, isSpacePressed, zoomLevel, panPosition]);

  // Handle drag-to-pan when zoomed in or space is held
  const handleMouseDown = (e: React.MouseEvent) => {
    // Allow panning when:
    // 1. Space key is held (takes priority over drawing mode), OR
    // 2. Zoomed in and not in drawing mode
    const canPan = isSpacePressed || (zoomLevel > 1 && !drawingMode);
    if (canPan && e.button === 0) {
      panStartRef.current = {
        x: e.clientX - panPosition.x,
        y: e.clientY - panPosition.y,
      };
      setIsPanning(true);
      e.preventDefault();
      e.stopPropagation(); // Prevent canvas from receiving the event
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    // Allow panning when space is held (takes priority) or zoomed in
    const canPan = isSpacePressed || zoomLevel > 1;
    if (isPanning && canPan) {
      setPanPosition({
        x: e.clientX - panStartRef.current.x,
        y: e.clientY - panStartRef.current.y,
      });
      e.preventDefault();
      e.stopPropagation(); // Prevent canvas from receiving the event
    }
  };

  // Use document-level listeners for panning to work even when mouse leaves container
  useEffect(() => {
    if (!isPanning) return;

    const handleMouseMoveDocument = (e: MouseEvent) => {
      // Allow panning when space is held (takes priority) or zoomed in
      // Use refs to get latest values without stale closures
      const canPan = isSpacePressed || zoomLevel > 1;
      if (canPan) {
        setPanPosition({
          x: e.clientX - panStartRef.current.x,
          y: e.clientY - panStartRef.current.y,
        });
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleMouseUpDocument = () => {
      // Only stop panning if space is not pressed
      if (!isSpacePressed) {
        setIsPanning(false);
      }
    };

    document.addEventListener('mousemove', handleMouseMoveDocument);
    document.addEventListener('mouseup', handleMouseUpDocument);

    return () => {
      document.removeEventListener('mousemove', handleMouseMoveDocument);
      document.removeEventListener('mouseup', handleMouseUpDocument);
    };
  }, [isPanning, isSpacePressed, zoomLevel]);

  const handleMouseUp = () => {
    // Only stop panning if space is not pressed
    // If space is still pressed, keep panning active
    if (!isSpacePressed) {
      setIsPanning(false);
    }
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error(
        translate(
          'bugReport.descriptionRequired',
          'Please provide a description'
        )
      );
      return;
    }

    try {
      setIsSubmitting(true);

      // Collect all bug report data
      const consoleLogs = collectConsoleLogs();
      const networkLogs = collectNetworkLogs();
      const currentUrl = getCurrentUrl();
      const browserInfo = getBrowserInfo();

      // Get API root from config
      const { configs } = await import('../configs');
      const apiRoot = configs.apiRoot || '/api';

      // Get annotated screenshot from canvas if available
      let finalScreenshot = screenshot;
      if (getCanvasDataUrlRef.current) {
        const annotatedScreenshot = getCanvasDataUrlRef.current();
        if (annotatedScreenshot) {
          finalScreenshot = annotatedScreenshot;
        }
      }

      // Prepare bug report payload
      // Convert annotation markers [[1: content]], [[2: content]], etc. back to "1. content" for submission
      const formattedDescription = description.replace(
        /\[\[(\d+):\s*([^\]]+)\]\]/g,
        (match, num, content) => {
          return `${num}. ${content.trim()} `;
        }
      );

      const bugReportData = {
        description: formattedDescription,
        expectation: expectation.trim() || undefined,
        screenshot: finalScreenshot || undefined,
        consoleLogs,
        networkLogs,
        environment: {
          url: currentUrl,
          userAgent: browserInfo.userAgent,
          platform: browserInfo.platform,
          screenWidth: browserInfo.screenWidth,
          screenHeight: browserInfo.screenHeight,
          windowWidth: browserInfo.windowWidth,
          windowHeight: browserInfo.windowHeight,
        },
        user: {
          roles: user?.roles || [],
        },
      };

      // Send bug report via API
      const response = await fetch(`${apiRoot}/bug-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bugReportData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Bug report API error:', {
          status: response.status,
          statusText: response.statusText,
          errorData,
        });
        throw new Error(
          errorData.message || errorData.error || 'Failed to send bug report'
        );
      }

      toast.success(
        translate('bugReport.sentSuccessfully', 'Bug report sent successfully')
      );

      // Reset form
      setDescription('');
      setExpectation('');
      setScreenshot(null);
      setScreenshotVersion(0);
      setAnnotations([]);
      setDrawingMode(null);
      onClose();
    } catch (error) {
      console.error('Failed to submit bug report:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : translate('bugReport.submitFailed', 'Failed to submit bug report')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] flex flex-col bug-report-dialog"
        data-bug-report-dialog="true"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{translate('bugReport.title')}</DialogTitle>
          <DialogDescription>
            {translate('bugReport.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-y-auto min-h-0">
          {/* Screenshot Section */}
          <div className="space-y-2">
            <Label>{translate('bugReport.screenshot')}</Label>
            <div className="flex gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                onClick={handleCaptureScreenshot}
                disabled={isCapturing}
              >
                {isCapturing
                  ? translate('bugReport.capturing')
                  : translate('bugReport.captureScreenshot')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
              >
                {translate('bugReport.uploadScreenshot')}
              </Button>
              {isBlurActive && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (isRevealed) {
                      hideRevealedData();
                    } else {
                      setShowReAuthDialog(true);
                    }
                  }}
                  className="flex items-center gap-2"
                >
                  {isRevealed ? (
                    <>
                      <EyeOff className="h-4 w-4" />
                      {translate(
                        'bugReport.hideSensitiveData',
                        'Hide Sensitive Data'
                      )}
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4" />
                      {translate(
                        'bugReport.revealSensitiveData',
                        'Reveal Sensitive Data'
                      )}
                    </>
                  )}
                </Button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>

            {isCapturing && !screenshot && (
              <div className="flex items-center justify-center p-8 border rounded-lg bg-gray-50">
                <p className="text-muted-foreground">
                  {translate('bugReport.capturing', 'Capturing screenshot...')}
                </p>
              </div>
            )}

            {screenshot && screenshot.length > 100 && (
              <div
                ref={containerRef}
                className="relative border rounded-lg"
                style={{
                  maxHeight: '600px',
                  overflow: 'hidden',
                  width: '100%',
                  cursor: isSpacePressed
                    ? isPanning
                      ? 'grabbing'
                      : 'grab'
                    : zoomLevel > 1 && !drawingMode
                    ? isPanning
                      ? 'grabbing'
                      : 'grab'
                    : drawingMode
                    ? 'crosshair'
                    : 'default',
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                <div
                  style={{
                    display: 'block',
                    width: '100%',
                    transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel})`,
                    transformOrigin: '0 0',
                  }}
                >
                  <ScreenshotAnnotationCanvas
                    key={`screenshot-${screenshotVersion}`}
                    imageSrc={screenshot}
                    annotations={annotations}
                    onAnnotationAdd={handleAnnotationAdd}
                    onAnnotationRemove={handleRemoveAnnotation}
                    drawingMode={isSpacePressed ? null : drawingMode}
                    zoomLevel={zoomLevel}
                    onCanvasReady={(getCanvasDataUrl) => {
                      getCanvasDataUrlRef.current = getCanvasDataUrl;
                    }}
                  />
                </div>
                <div className="absolute top-2 left-2 flex gap-2 bg-popover/90 border border-border text-popover-foreground shadow-sm backdrop-blur-sm p-2 rounded z-10">
                  <Button
                    type="button"
                    size="sm"
                    variant={drawingMode === 'circle' ? 'default' : 'outline'}
                    onClick={() =>
                      setDrawingMode(drawingMode === 'circle' ? null : 'circle')
                    }
                    title={translate('bugReport.drawCircle')}
                  >
                    <Circle className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={drawingMode === 'arrow' ? 'default' : 'outline'}
                    onClick={() =>
                      setDrawingMode(drawingMode === 'arrow' ? null : 'arrow')
                    }
                    title={translate('bugReport.drawArrow')}
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={
                      drawingMode === 'rectangle' ? 'default' : 'outline'
                    }
                    onClick={() =>
                      setDrawingMode(
                        drawingMode === 'rectangle' ? null : 'rectangle'
                      )
                    }
                    title={translate('bugReport.drawRectangle')}
                  >
                    <Square className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={drawingMode === 'custom' ? 'default' : 'outline'}
                    onClick={() =>
                      setDrawingMode(drawingMode === 'custom' ? null : 'custom')
                    }
                    title={translate(
                      'bugReport.drawCustom',
                      'Draw Custom Shape'
                    )}
                  >
                    <PenTool className="h-4 w-4" />
                  </Button>
                  {annotations.length > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleClearAnnotations}
                      title={translate(
                        'bugReport.clearAnnotations',
                        'Clear All Annotations'
                      )}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="absolute top-2 right-2 flex gap-2 bg-popover/90 border border-border text-popover-foreground shadow-sm backdrop-blur-sm p-2 rounded z-10">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const newZoom = Math.max(0.25, zoomLevel - 0.25);
                      setZoomLevel(newZoom);
                      if (newZoom === 1) {
                        setPanPosition({ x: 0, y: 0 });
                      }
                    }}
                    title={translate('bugReport.zoomOut', 'Zoom Out')}
                  >
                    <ZoomOut className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setZoomLevel(1);
                      setPanPosition({ x: 0, y: 0 });
                    }}
                    title={translate('bugReport.resetZoom', 'Reset Zoom')}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.25))}
                    title={translate('bugReport.zoomIn', 'Zoom In')}
                  >
                    <ZoomIn className="h-4 w-4" />
                  </Button>
                  <span className="text-sm px-2 py-1 text-muted-foreground">
                    {Math.round(zoomLevel * 100)}%
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Description with inline annotations */}
          <div className="space-y-2">
            <Label htmlFor="description">
              {translate('bugReport.descriptionLabel')} *
            </Label>
            <div className="relative border rounded-md min-h-[100px] bg-background">
              <div
                ref={descriptionEditableRef}
                className="w-full min-h-[100px] p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 overflow-y-auto"
                contentEditable
                suppressContentEditableWarning
                onInput={(e) => {
                  if (isUpdatingDescriptionRef.current) return;

                  // Extract text, but skip text nodes inside annotation badges
                  const div = e.currentTarget;
                  let extractedText = '';

                  // Helper to check if a node is inside a badge element
                  const isInsideBadge = (node: Node): boolean => {
                    let parent = node.parentElement;
                    while (parent && parent !== div) {
                      if (parent.hasAttribute('data-annotation')) {
                        return true;
                      }
                      parent = parent.parentElement;
                    }
                    return false;
                  };

                  const walker = document.createTreeWalker(
                    div,
                    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
                    {
                      acceptNode: (node) => {
                        // Skip text nodes that are inside badge elements
                        if (
                          node.nodeType === Node.TEXT_NODE &&
                          isInsideBadge(node)
                        ) {
                          return NodeFilter.FILTER_REJECT;
                        }
                        return NodeFilter.FILTER_ACCEPT;
                      },
                    }
                  );
                  let node;
                  const existingMarkers = parseAnnotationMarkers(
                    descriptionRef.current || description
                  );
                  const seenMarkerNumbers = new Set<number>();

                  while ((node = walker.nextNode())) {
                    if (node.nodeType === Node.TEXT_NODE) {
                      // This is user-typed text, not inside a badge
                      // textContent preserves newlines, so we can use it directly
                      extractedText += node.textContent;
                    } else if (node.nodeType === Node.ELEMENT_NODE) {
                      const element = node as Element;

                      // Handle <br> tags - they represent newlines
                      if (element.tagName === 'BR') {
                        extractedText += '\n';
                      }
                      // Handle block elements (div, p) - add newline if not already there
                      else if (
                        element.tagName === 'DIV' ||
                        element.tagName === 'P'
                      ) {
                        // Only add newline if previous content didn't end with one
                        if (extractedText && !extractedText.endsWith('\n')) {
                          extractedText += '\n';
                        }
                      }
                      // Handle badge elements
                      else if (element.hasAttribute('data-annotation')) {
                        const num = element.getAttribute('data-annotation');
                        if (num) {
                          const markerNum = parseInt(num, 10);
                          // Only add marker if we haven't seen this number yet (prevent duplicates)
                          if (!seenMarkerNumbers.has(markerNum)) {
                            seenMarkerNumbers.add(markerNum);
                            const existingMarker = existingMarkers.find(
                              (m) => m.number === markerNum
                            );
                            if (existingMarker) {
                              // Use existing marker from description
                              extractedText += `[[${markerNum}: ${existingMarker.content}]]`;
                            } else {
                              // New marker not in description yet, extract from badge
                              const badgeElement = element;
                              const typeSpan =
                                badgeElement.querySelector('.text-red-600');
                              const content =
                                typeSpan?.textContent?.trim() || '';
                              extractedText += `[[${markerNum}: ${content}]]`;
                            }
                          }
                        }
                      }
                    }
                  }

                  // Deduplicate any remaining markers in the extracted text
                  const finalMarkers = parseAnnotationMarkers(extractedText);
                  const uniqueMarkers = new Map<number, string>();
                  finalMarkers.forEach((m) => {
                    if (!uniqueMarkers.has(m.number)) {
                      uniqueMarkers.set(m.number, m.content);
                    }
                  });

                  // Rebuild text: keep text parts, replace all markers with unique ones
                  let deduplicatedText = '';
                  const parts = extractedText.split(/(\[\[\d+:\s*[^\]]+\]\])/);
                  const seenInRebuild = new Set<number>();

                  parts.forEach((part) => {
                    const match = part.match(/\[\[(\d+):\s*([^\]]+)\]\]/);
                    if (match) {
                      const num = parseInt(match[1], 10);
                      if (!seenInRebuild.has(num) && uniqueMarkers.has(num)) {
                        seenInRebuild.add(num);
                        deduplicatedText += `[[${num}: ${uniqueMarkers.get(
                          num
                        )}]]`;
                      }
                      // Skip duplicate markers
                    } else {
                      deduplicatedText += part;
                    }
                  });

                  setDescription(deduplicatedText);
                  descriptionRef.current = deduplicatedText;
                }}
                onBlur={(e) => {
                  if (isUpdatingDescriptionRef.current) return;

                  // Same logic on blur - skip text nodes inside badges
                  const div = e.currentTarget;
                  let text = '';

                  const isInsideBadge = (node: Node): boolean => {
                    let parent = node.parentElement;
                    while (parent && parent !== div) {
                      if (parent.hasAttribute('data-annotation')) {
                        return true;
                      }
                      parent = parent.parentElement;
                    }
                    return false;
                  };

                  const walker = document.createTreeWalker(
                    div,
                    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
                    {
                      acceptNode: (node) => {
                        if (
                          node.nodeType === Node.TEXT_NODE &&
                          isInsideBadge(node)
                        ) {
                          return NodeFilter.FILTER_REJECT;
                        }
                        return NodeFilter.FILTER_ACCEPT;
                      },
                    }
                  );
                  let node;
                  const existingMarkers = parseAnnotationMarkers(
                    descriptionRef.current || description
                  );

                  while ((node = walker.nextNode())) {
                    if (node.nodeType === Node.TEXT_NODE) {
                      // textContent preserves newlines
                      text += node.textContent;
                    } else if (node.nodeType === Node.ELEMENT_NODE) {
                      const element = node as Element;

                      // Handle <br> tags - they represent newlines
                      if (element.tagName === 'BR') {
                        text += '\n';
                      }
                      // Handle block elements (div, p) - add newline if not already there
                      else if (
                        element.tagName === 'DIV' ||
                        element.tagName === 'P'
                      ) {
                        if (text && !text.endsWith('\n')) {
                          text += '\n';
                        }
                      }
                      // Handle badge elements
                      else if (element.hasAttribute('data-annotation')) {
                        const num = element.getAttribute('data-annotation');
                        if (num) {
                          const existingMarker = existingMarkers.find(
                            (m) => m.number === parseInt(num, 10)
                          );
                          if (existingMarker) {
                            text += `[[${num}: ${existingMarker.content}]]`;
                          } else {
                            const badgeElement = element;
                            const typeSpan =
                              badgeElement.querySelector('.text-red-600');
                            const content = typeSpan?.textContent?.trim() || '';
                            text += `[[${num}: ${content}]]`;
                          }
                        }
                      }
                    }
                  }
                  setDescription(text);
                  descriptionRef.current = text;

                  // Trigger re-render on blur to sync badges
                  isUpdatingDescriptionRef.current = true;
                  const textMarkers = parseAnnotationMarkers(text);
                  const html = text
                    .split(/(\[\[\d+:\s*[^\]]+\]\])/)
                    .map((part, index, array) => {
                      const match = part.match(/\[\[(\d+):\s*([^\]]+)\]\]/);
                      if (match) {
                        const annotationNumber = parseInt(match[1], 10);
                        const content = match[2].trim();
                        const marker = textMarkers.find(
                          (m) => m.number === annotationNumber
                        );
                        const annotation = annotations.find(
                          (ann) => ann.number === annotationNumber
                        );
                        if (marker && annotation) {
                          // Check if we need to add a newline before this badge
                          const prevPart = index > 0 ? array[index - 1] : '';
                          const needsNewline =
                            prevPart &&
                            !prevPart.endsWith('\n') &&
                            !prevPart.match(/<br\s*\/?>$/);
                          const newlineBefore = needsNewline ? '<br>' : '';

                          return `${newlineBefore}<span data-annotation="${annotationNumber}" class="inline-flex items-center gap-1.5 px-1.5 py-0.5 mx-1 bg-blue-50 border border-blue-200 rounded-md text-xs font-medium align-middle shadow-sm" contenteditable="false">
                            <span class="flex items-center justify-center w-4 h-4 rounded-full bg-blue-500 text-white text-[10px] font-bold leading-none">${annotationNumber}</span>
                            <span class="text-blue-700 capitalize">${content}</span>
                            <button type="button" class="flex items-center justify-center ml-0.5 hover:bg-blue-200 rounded p-0.5 transition-colors opacity-70 hover:opacity-100" data-remove-annotation="${annotationNumber}" title="${translate(
                            'bugReport.removeAnnotation',
                            'Remove this annotation'
                          )}">
                              <svg class="h-2.5 w-2.5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </span>`;
                        }
                        return part
                          .replace(/&/g, '&amp;')
                          .replace(/</g, '&lt;')
                          .replace(/>/g, '&gt;');
                      }
                      return part
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/\n/g, '<br>');
                    })
                    .join('');
                  div.innerHTML = html;

                  // Re-attach click handlers
                  div
                    .querySelectorAll('[data-remove-annotation]')
                    .forEach((button) => {
                      const newButton = button.cloneNode(true);
                      button.parentNode?.replaceChild(newButton, button);
                      newButton.addEventListener('click', (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const num = parseInt(
                          (newButton as Element).getAttribute(
                            'data-remove-annotation'
                          ) || '0',
                          10
                        );
                        if (num) {
                          handleRemoveAnnotation(num);
                        }
                      });
                    });

                  setTimeout(() => {
                    isUpdatingDescriptionRef.current = false;
                  }, 0);
                }}
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              />
            </div>
            {/* Debug: Show raw description */}
            <div className="mt-2 p-2 bg-gray-100 border rounded text-xs font-mono hidden">
              <div className="font-semibold mb-1">Raw Description (Debug):</div>
              <div className="whitespace-pre-wrap break-all">
                {description || '(empty)'}
              </div>
            </div>

            {/* Hidden textarea for form submission */}
            <textarea
              id="description"
              value={description.replace(
                /\[\[(\d+):\s*([^\]]+)\]\]/g,
                (match, num, content) => {
                  return `${num}. ${content.trim()}`;
                }
              )}
              readOnly
              className="hidden"
              aria-hidden="true"
            />
          </div>

          {/* Expected Behavior */}
          <div className="space-y-2">
            <Label htmlFor="expectation">
              {translate('bugReport.expectationLabel')}
            </Label>
            <Textarea
              id="expectation"
              value={expectation}
              onChange={(e) => setExpectation(e.target.value)}
              placeholder={translate('bugReport.expectationPlaceholder')}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button type="button" variant="outline" onClick={onClose}>
            {translate('pos.common.action.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !description.trim()}
          >
            {isSubmitting
              ? translate('bugReport.submitting')
              : translate('bugReport.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Re-authentication Dialog */}
      <ReAuthenticationDialog
        open={showReAuthDialog}
        onClose={() => setShowReAuthDialog(false)}
        onSuccess={() => {
          revealBlurredData();
          // Re-capture screenshot with revealed data
          setTimeout(() => {
            handleCaptureScreenshot();
          }, 200);
        }}
        title={translate('bugReport.reauthTitle', 'Re-authentication Required')}
        description={translate(
          'bugReport.reauthDescription',
          'Please enter your password to reveal sensitive data in the screenshot. This is required for security purposes.'
        )}
      />
    </Dialog>
  );
};
